import { defaultBusiness as NAP } from "@/lib/site-config";
import { createFileRoute } from "@tanstack/react-router";
import { HelpfulPricingGuides } from "@/components/HelpfulPricingGuides";
import { Phone, MessageCircle, CheckCircle, AlertTriangle, ArrowRight } from "lucide-react";
import { Link } from "@tanstack/react-router";
import { services, getBusinessConfig } from "@/lib/site-config";
import { faqSchema, FAQAccordion } from "@/components/FAQAccordion";
import { HelpfulGuidesGrid } from "@/components/HelpfulGuidesGrid";
import { ServicePageTemplate } from "@/components/ServicePageTemplate";
import { AnimatedSection, StaggerContainer, StaggerItem } from "@/components/AnimatedSection";
import { ReassuranceBanner } from "@/components/ReassuranceBanner";
import { ServiceHero } from "@/components/ServiceHero";
import { ProcessSteps } from "@/components/ProcessSteps";
import { CTABlock } from "@/components/CTABlock";
import { seoHead, serviceSchema, localBusinessSchema, complianceToBusinessOpts } from "@/lib/seo";
import { getPublicConfig } from "@/lib/site-content.functions";
import { PrimaryCTA } from "@/components/PrimaryCTA";
import { ServiceTestimonials } from "@/components/ServiceTestimonials";
import { InternalLinksBlock } from "@/components/InternalLinksBlock";
import { DriverSupportCTA } from "@/components/DriverSupportCTA";
import { WhyChooseWoodseer } from "@/components/WhyChooseWoodseer";
import { MOTPrepChecklistBlock } from "@/components/MOTPrepChecklistBlock";

const motService = services[0];

const motClassesFaqs = [
  ...motService.faqs,
  { q: "What MOT classes do you test?", a: "We test Class 1 & 2 (motorcycles), Class 3 (3-wheelers), Class 4 (cars up to 3,000kg), Class 5 (larger passenger vehicles) and Class 7 (goods vehicles up to 3,500kg). Call us to confirm your vehicle." },
  { q: "Can I combine my MOT with a service?", a: "Yes — and we'd recommend it. Booking your MOT and service together saves you a trip and we can often offer a combined deal. Just let us know when you call." },
  { q: "What should I check before my MOT?", a: "Top up your screenwash, check all lights work, make sure your tyres have at least 1.6mm tread, and clean your number plates so they're legible. These simple checks help avoid easy fails." },
  { q: "How much does an MOT cost?", a: "Class 4 cars are £30. Larger vehicles (Class 5 and 7) are £40. Motorcycles (Class 1 & 2) are £29.65. We charge the DVSA-set maximum and never add hidden fees — a re-test within 10 working days is free if you bring the car back to us." },
  { q: "How long does an MOT take?", a: `Most MOTs take 45–60 minutes. Drop the car off and we'll usually call within the hour with the result. Same-day slots are often available — call ${NAP.phone} to check.` },
  { q: "What's included in the MOT?", a: "A full DVSA inspection of lights, brakes, suspension, steering, tyres, exhaust emissions, seatbelts, wipers, mirrors, body condition and VIN. You get a digital certificate and a written advisory list of anything to watch — no surprise repair pressure." },
];


export const Route = createFileRoute("/mot")({
  loader: async () => {
    const cfg = await getPublicConfig().catch(() => null);
    return { compliance: cfg?.compliance || null };
  },
  head: ({ loaderData }) => seoHead({
    path: "/mot",
    title: "MOT Testing East London E14 | DVSA Station | Woodseer Autos",
    description: `DVSA-authorised MOT station V106376 in Poplar E14. MOTs typically £30–£40. Classes 1–5 & 7. Fast, honest, fair pricing. Call ${NAP.phone} to book.`,
    breadcrumbs: [
      { name: "Home", path: "/" },
      { name: "MOT Testing", path: "/mot" },
    ],
    jsonLd: [
      localBusinessSchema(complianceToBusinessOpts(loaderData?.compliance)),
      faqSchema(motClassesFaqs),
      serviceSchema({
        name: "MOT Testing — Woodseer Autos",
        serviceType: "MOT Testing",
        description: "DVSA-authorised MOT testing station V106376. Testing classes 1, 2, 3, 4, 5 and 7 in Poplar, East London. MOTs typically £30–£40 — call to confirm vehicle class.",
        offers: { price: "30.00", priceCurrency: "GBP", description: "From £30 for Class 4 MOT; £30–£40 depending on vehicle class." },
      }),
    ],
  }),
  component: MOTPage,
});

function MOTPage() {
  const biz = getBusinessConfig();

  return (
    <>
      <ServiceHero
        headline={motService.headline}
        intro={motService.intro}
        image="/images/hero-mot.jpg"
        imageAlt="MOT testing at Woodseer Autos, East London"
      />

      {/* Book Now Strip */}
      <section className="border-y border-border bg-primary/5 py-5">
        <div className="mx-auto max-w-4xl px-4 flex flex-col sm:flex-row items-center justify-between gap-3">
          <div className="text-center sm:text-left">
            <p className="text-base font-semibold text-foreground">
              Book your MOT today — most tests completed within the hour
            </p>
            <p className="mt-1 text-xs text-muted-foreground">
              MOTs typically £30–£40. Call to confirm availability and vehicle class. No work without your approval.
            </p>
          </div>
          <div className="flex flex-wrap items-center justify-center gap-2 shrink-0">
            <PrimaryCTA location="mot-book-strip" intent="MOT quote" variant="light" />
          </div>
        </div>
      </section>

      <ReassuranceBanner />

      {/* MOT Classes */}
      <AnimatedSection>
        <section className="py-16 md:py-20 bg-card">
          <div className="mx-auto max-w-4xl px-4">
            <h2 className="text-3xl font-bold text-foreground text-center">MOT Classes We Test</h2>
            <p className="mt-3 text-center text-muted-foreground">We're authorised to test a wide range of vehicle types.</p>
            <StaggerContainer className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-2xl mx-auto">
              {[
                { cls: "Class 1 & 2", desc: "Motorcycles — all engine sizes" },
                { cls: "Class 3", desc: "Three-wheeled vehicles" },
                { cls: "Class 4", desc: "Cars and light vans up to 3,000kg" },
                { cls: "Class 5", desc: "Larger passenger vehicles (13+ seats)" },
                { cls: "Class 7", desc: "Goods vehicles between 3,000kg and 3,500kg" },
              ].map((c) => (
                <StaggerItem key={c.cls}>
                  <div className="flex items-start gap-3 rounded-xl border border-border p-4 bg-background/50">
                    <CheckCircle className="h-4 w-4 text-primary shrink-0 mt-1" />
                    <div>
                      <span className="text-sm font-semibold text-foreground">{c.cls}</span>
                      <p className="text-sm text-muted-foreground">{c.desc}</p>
                    </div>
                  </div>
                </StaggerItem>
              ))}
            </StaggerContainer>
          </div>
        </section>
      </AnimatedSection>

      {/* What's Included */}
      <AnimatedSection>
        <section className="py-16 md:py-20">
          <div className="mx-auto max-w-4xl px-4">
            <h2 className="text-3xl font-bold text-foreground text-center">What's Included</h2>
            <StaggerContainer className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-2xl mx-auto">
              {motService.included.map((item) => (
                <StaggerItem key={item}>
                  <div className="flex items-start gap-3 text-sm text-muted-foreground">
                    <CheckCircle className="h-4 w-4 text-primary shrink-0 mt-0.5" />
                    {item}
                  </div>
                </StaggerItem>
              ))}
            </StaggerContainer>
          </div>
        </section>
      </AnimatedSection>

      {/* What Happens if You Fail */}
      <AnimatedSection>
        <section className="py-16 md:py-20 bg-card">
          <div className="mx-auto max-w-4xl px-4">
            <h2 className="text-3xl font-bold text-foreground text-center">What Happens if Your Vehicle Fails?</h2>
            <p className="mt-3 text-center text-muted-foreground">Failing an MOT isn't the end of the world. Here's how we handle it.</p>
            <div className="mt-8 space-y-4 max-w-2xl mx-auto">
              {[
                { title: "We explain exactly what failed", desc: "No jargon, no scare tactics. We show you the issue and explain what needs fixing in plain English." },
                { title: "You get a clear, honest quote", desc: "We'll quote for the repairs before any work starts. No obligation — it's your call." },
                { title: "We fix it on-site", desc: "Most MOT failures can be repaired right here in our workshop. No need to take your car elsewhere." },
                { title: "Free retest", desc: "If we carry out the repairs, we'll retest your vehicle for free. You only pay for the work." },
              ].map((item, i) => (
                <div key={i} className="flex gap-4 p-5 rounded-xl border border-border bg-background/50">
                  <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary/10 text-primary text-sm font-bold shrink-0">
                    {i + 1}
                  </div>
                  <div>
                    <h3 className="font-semibold text-foreground">{item.title}</h3>
                    <p className="mt-1 text-sm text-muted-foreground">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      </AnimatedSection>

      {/* MOT + Service Combo */}
      <AnimatedSection>
        <section className="py-16 md:py-20">
          <div className="mx-auto max-w-4xl px-4 text-center">
            <h2 className="text-3xl font-bold text-foreground">Combine Your MOT with a Service</h2>
            <p className="mt-3 text-muted-foreground max-w-2xl mx-auto">
              Many customers book their MOT and annual service together. It saves a trip, lets us catch issues early, and you'll often get a better deal. We'll stamp your service book and give you a full vehicle health report at the same time.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3">
              <a href={biz.phoneHref} className="inline-flex items-center gap-2 rounded-md bg-primary px-6 py-3 text-base font-semibold text-primary-foreground hover:bg-primary/90 transition-colors">
                <Phone className="h-5 w-5" />
                Book MOT + Service
              </a>
              <Link to="/servicing" className="inline-flex items-center gap-1 text-primary font-medium hover:text-primary/80 transition-colors">
                View servicing options <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </section>
      </AnimatedSection>

      {/* Pre-MOT Checklist (reusable component) */}
      <MOTPrepChecklistBlock />

      {/* Common Fail Reasons */}
      <AnimatedSection>
        <section className="py-16 md:py-20">
          <div className="mx-auto max-w-4xl px-4">
            <h2 className="text-3xl font-bold text-foreground text-center">Common MOT Fail Reasons</h2>
            <StaggerContainer className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-2xl mx-auto">
              {motService.commonIssues.map((issue) => (
                <StaggerItem key={issue}>
                  <div className="flex items-start gap-3 rounded-xl border border-border p-4 text-sm text-muted-foreground bg-card/50">
                    <AlertTriangle className="h-4 w-4 text-primary shrink-0 mt-0.5" />
                    {issue}
                  </div>
                </StaggerItem>
              ))}
            </StaggerContainer>
          </div>
        </section>
      </AnimatedSection>

      {/* Why Choose Us */}
      <AnimatedSection>
        <section className="py-16 md:py-20 bg-card">
          <div className="mx-auto max-w-4xl px-4">
            <h2 className="text-3xl font-bold text-foreground text-center">Why Choose Woodseer Autos for Your MOT</h2>
            <StaggerContainer className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-2xl mx-auto">
              {motService.whyUs.map((reason) => (
                <StaggerItem key={reason}>
                  <div className="flex items-start gap-3 text-sm text-muted-foreground">
                    <CheckCircle className="h-4 w-4 text-primary shrink-0 mt-0.5" />
                    {reason}
                  </div>
                </StaggerItem>
              ))}
            </StaggerContainer>
          </div>
        </section>
      </AnimatedSection>

      {/* Process */}
      <AnimatedSection>
        <ProcessSteps steps={motService.process} title="How Your MOT Works" />
      </AnimatedSection>

      {/* FAQs */}
      <AnimatedSection>
      <HelpfulGuidesGrid slugs={["mot-warning-lights-before-test","tyre-check-before-mot","brake-noise-squeaking-grinding"]} title="Helpful guides for this service" compact />

        <FAQAccordion faqs={motClassesFaqs} />
      </AnimatedSection>

      {/* Testimonials with Review + AggregateRating schema */}
      <AnimatedSection>
        <ServiceTestimonials
          service="MOT"
          title="What MOT customers say"
          itemReviewedType="Service"
          itemReviewedName="MOT Testing — Woodseer Autos"
          serviceType="MOT Testing"
        />
      </AnimatedSection>

      {/* Internal links: sibling services + local areas (MOT in area) */}
      <AnimatedSection>
        <InternalLinksBlock currentService="mot" serviceForAreaLinks="mot" />
      </AnimatedSection>

      <HelpfulPricingGuides currentService="mot" />
      <div className="mx-auto max-w-4xl px-4"><DriverSupportCTA page="mot" service="mot" bookingContext="mot-driver-support" /></div>
      <WhyChooseWoodseer page="mot" />
      <CTABlock headline="Book Your MOT Today" subtext="Call us now to book your MOT at a time that suits you. Most tests done within the hour." />
    </>
  );
}
